<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ item?.id ? $t('employeeEditModalTitle') : $t('employeeCreateModalTitle') }}</div>
    </template>

    <Spinner v-show="dataIsSending"/>
    <form v-show="!dataIsSending" autocomplete="off" @submit.prevent="saveItem">
      <div class="p-formgrid p-grid p-pt-3">
        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label p-input-icon-right">
            <i class="pi pi-user" />
            <InputText id="employee-first-name"
                       @input="onNameInput('firstName', $event)"
                       @keydown="onNameKeyDown"
                       @paste="onNamePaste"
                       :class="{'p-invalid' : submitted && !firstName}"
                       v-model.trim="firstName"
                       autocomplete="off"/>
            <label for="employee-first-name">{{ $t('First name') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !firstName">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label p-input-icon-right">
            <i class="pi pi-user" />
            <InputText id="employee-last-name"
                       @input="onNameInput('lastName', $event)"
                       @keydown="onNameKeyDown"
                       @paste="onNamePaste"
                       v-model.trim="lastName"
                       autocomplete="off"/>
            <label for="employee-last-name">{{ $t('Last name') }}</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <PhoneInput v-model:phoneCountry="userData.phone_country"
                      v-model:phoneCode="userData.phone_code"
                      v-model:phoneNumber="userData.phone_number"
                      v-model:isInvalid="phoneInvalid"
                      :required="true"
                      :visible="visible"
                      :modalData="userData"
                      :submitted="submitted"/>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <EmailInput v-model="userData.email"
                      v-model:isInvalid="emailInvalid"
                      :submitted="submitted"
                      inputId="user-email"/>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <PhoneInput v-model:phoneCountry="employeeData.work_phone_country"
                      v-model:phoneCode="employeeData.work_phone_code"
                      v-model:phoneNumber="employeeData.work_phone_number"
                      v-model:isInvalid="workPhoneInvalid"
                      :name="'employee-work-'"
                      :label="'Work Phone'"
                      :visible="visible"
                      :modalData="userData"
                      :submitted="submitted"/>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <CustomDropdown id="employee_language"
                            :class="{'p-invalid' : submitted && !selectedLanguage}"
                            v-model="selectedLanguage"
                            :options="languageValues"
                            :clearSearchData="!visible"
                            data-key="value"
                            :filter="true"
                            :filterFields="['name', 'value', 'label']"
                            :showClear="false">
              <template #value="slotProps">
                <div class="country-item country-item-value" v-if="slotProps.value">
                  {{ formatLanguageName(slotProps.value) }}
                </div>
                <span v-else style="visibility: hidden">.</span>
              </template>
              <template #option="slotProps">
                <div>{{ formatLanguageName(slotProps.option) }}</div>
              </template>
            </CustomDropdown>
            <label for="employee_language">{{ $t('Language') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !selectedLanguage">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputText id="personal_code" v-model.trim="employeeData.personal_code" autocomplete="off"/>
            <label for="personal_code">{{ $t('Personal code') }}</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputText id="contract" v-model.trim="employeeData.contract" autocomplete="off"/>
            <label for="contract">{{ $t('Contract') }}</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Calendar id="contract_start"
                      appendTo="self"
                      class="calendar-bottom"
                      @show="toggleDropdownOverlayVisibility(true)"
                      @hide="toggleDropdownOverlayVisibility(false)"
                      @date-select="checkContractStartDateIsValid"
                      :class="{'p-invalid' : (employeeData.start && !contractStartDateIsValid) || (employeeData.start && employeeData.end && employeeData.start > employeeData.end)}"
                      v-model="employeeData.start"
                      :dateFormat="settings.dateFormat"
                      :showIcon="true"
                      :selectOtherMonths="true"
                      autocomplete="off"/>
            <label for="contract_start">{{ $t('Start') }}</label>
          </div>
          <small class="p-invalid" v-if="employeeData.start && !contractStartDateIsValid">{{ $t('Invalid date format') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Calendar id="contract_end"
                      appendTo="self"
                      class="calendar-bottom"
                      @date-select="checkContractEndDateIsValid"
                      @show="toggleDropdownOverlayVisibility(true)"
                      @hide="toggleDropdownOverlayVisibility(false)"
                      :class="{'p-invalid' : (employeeData.end && !contractEndDateIsValid) || (employeeData.start && employeeData.end && employeeData.start > employeeData.end)}"
                      v-model="employeeData.end"
                      :dateFormat="settings.dateFormat"
                      :showIcon="true"
                      :selectOtherMonths="true"
                      autocomplete="off"/>
            <label for="contract_end">{{ $t('End') }}</label>
          </div>
          <small class="p-invalid" v-if="employeeData.end && !contractEndDateIsValid">{{ $t('Invalid date format') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputText id="employee_position_modal" v-model.trim="employeeData.position" autocomplete="off"/>
            <label for="employee_position_modal">{{ $t('Position') }}</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Calendar id="employee-birthdate"
                      appendTo="self"
                      class="calendar-bottom"
                      @date-select="checkBirthDateIsValid"
                      @show="toggleDropdownOverlayVisibility(true)"
                      @hide="toggleDropdownOverlayVisibility(false)"
                      :class="{'p-invalid' : userData.birthdate && !birthDateIsValid}"
                      v-model="userData.birthdate"
                      :dateFormat="settings.dateFormat"
                      :showIcon="true"
                      :monthNavigator="true"
                      :yearNavigator="true"
                      :yearRange="(new Date().getFullYear() - 80) + ':' + (new Date().getFullYear() - 14)"
                      :selectOtherMonths="true"
                      autocomplete="off"/>
            <label for="employee-birthdate">{{ $t('Birthdate') }}</label>
          </div>
          <small class="p-invalid" v-if="userData.birthdate && !birthDateIsValid">{{ $t('Invalid date format') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5" v-if="!item?.id">
          <div class="p-float-label">
            <CustomDropdown id="employee_role_modal"
                            :disabled="userData.role === constants.userRoles.superAdmin"
                            v-model="selectedRole"
                            :options="roleOptions"
                            :clearSearchData="!visible"
                            optionLabel="name"
                            :class="{'p-invalid' : submitted && !selectedRole}"
                            :filter="true"
                            autocomplete="off"
                            :showClear="false">
              <template #value="slotProps">
                <div class="country-item country-item-value" v-if="slotProps.value">
                  <span>{{ slotProps.value.name }}</span>
                </div>
                <span v-else style="visibility: hidden">.</span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{ slotProps.option.name }}</span>
                </div>
              </template>
            </CustomDropdown>
            <label for="employee_role_modal">{{ $t('Role') }}<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !selectedRole">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
          <LimitedCharsTextarea :submitted="submitted"
                                :rows="3"
                                v-model="employeeData.comment"
                                :maxChars="commentMaxChars"
                                :autocomplete="'off'"
                                :id="'employee-modal-notes'"
                                :label="$t('Notes')"/>
        </div>
      </div>

      <div class="section-divider p-mt-3" v-if="!item?.id" :class="{ 'p-pb-3': !createAccount }">
        <div class="p-d-flex p-ai-center">
          <Checkbox id="create-user-account-checkbox" :binary="true" v-model="createAccount"/>
          <label for="create-user-account-checkbox" class="pointer p-ml-2">
            {{ $t('createUserAccountLabel') }}
          </label>
        </div>
        <hr class="section-divider__hr">
      </div>

      <div v-if="createAccount && !item?.id" class="p-formgrid p-grid p-mt-5">
        <div class="p-field p-col-12 p-mb-5">
          <div class="p-float-label">
            <InputText id="user_username"
                       name="emp_login"
                       :class="submitted && ((createAccount && !userData.username) ||
                         (userData.username && takenUsername && userData.username === takenUsername) ||
                         (userData.username && userData.username.length < settings.minUsernameLength) ||
                         (userData.username && userData.username.length > settings.maxUsernameLength)
                         ) ? 'p-invalid' : ''"
                       v-model.trim="userData.username"
                       :maxlength="settings.maxUsernameLength"
                       @blur="onUsernameBlur"
                       @paste="onUsernamePaste"
                       autocomplete="new-password"/>
            <label for="user_username">
              {{ $t('Username') }}<span v-if="createAccount" class="warning-color">*</span>
            </label>
          </div>
          <small class="p-invalid" v-if="submitted && createAccount && !userData.username">
            {{ $t('Required field') }}
          </small>
          <small class="p-invalid" v-else-if="submitted && userData.username && takenUsername && userData.username === takenUsername">
            {{ $t('usernameTaken') }}
          </small>
          <small class="p-invalid" v-else-if="submitted && userData.username && userData.username.length < settings.minUsernameLength">
            {{ $t('tooShortMinChars', { min: settings.minUsernameLength }) }}
          </small>
          <small v-else-if="submitted && userData.username &&
                 userData.username.length > settings.maxUsernameLength"
                 class="p-invalid">
            {{ $t('maxCharsAllowedMsg', { max: settings.maxUsernameLength }) }}
          </small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Password id="employee_password"
                      name="emp_pwd"
                      v-model="userData.password"
                      :inputClass="createAccount && submitted && (
                          !userData.password ||
                          userData.password.length < settings.minPasswordLength ||
                          userData.password.length > settings.maxPasswordLength ||
                          passwordServerError
                        ) ? 'p-invalid' : ''"
                      :feedback="true"
                      toggleMask
                      autocomplete="new-password"/>
            <label for="employee_password">
              {{ $t('Password') }}<span class="warning-color">*</span>
            </label>
          </div>
          <small class="p-invalid" v-if="submitted && createAccount && !userData.password">
            {{ $t('Required field') }}
          </small>
          <small class="p-invalid" v-else-if="submitted && createAccount && (userData.password && userData.password.length < settings.minPasswordLength)">
            {{ $t('tooShortMinChars', { min: settings.minPasswordLength }) }}
          </small>
          <small v-else-if="submitted && createAccount && userData.password &&
                  userData.password.length > settings.maxPasswordLength"
                 class="p-invalid">
            {{ $t('maxCharsAllowedMsg', { max: settings.maxPasswordLength }) }}
          </small>
          <small v-else-if="submitted && createAccount && passwordServerError"
                 class="p-invalid">
            {{ translatePasswordValidationError(passwordServerError) }}
          </small>
          <!--          <small class="p-invalid" v-else-if="submitted && createAccount && userData.password_repeat !== userData.password">-->
          <!--            {{ $t('Passwords do not match') }}-->
          <!--          </small>-->
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Password id="employee_password_repeat"
                      name="emp_pwd2"
                      :inputClass="createAccount && submitted && (!userData.password_repeat || userData.password_repeat !== userData.password) ? 'p-invalid' : ''"
                      v-model="userData.password_repeat"
                      :feedback="true"
                      toggleMask
                      autocomplete="new-password"/>
            <label for="employee_password_repeat">
              {{ $t('Repeat password') }}<span class="warning-color">*</span>
            </label>
          </div>
          <small class="p-invalid" v-if="submitted && createAccount && !userData.password_repeat">
            {{ $t('Required field') }}
          </small>
          <small class="p-invalid" v-else-if="submitted && createAccount && userData.password_repeat !== userData.password">
            {{ $t('Passwords do not match') }}
          </small>
        </div>
      </div>

      <div v-if="!item?.id && employeeExtraChargeRequired" class="p-mt-1 p-pb-3" :class="{'p-mt-4': !createAccount}">
        <InlineNotice severity="warning"
                      icon="pi pi-info-circle"
                      :title="$t('billing.extraResources.additionalEmployeeTitle')">
            {{ $t('billing.extraResources.additionalEmployeeNotice', {
            included: employeeResourceUsage.included_next,
            used: employeeResourceUsage.used,
            price: employeeResourceUsage.monthly_delta
          }) }}
          <template #actions>
            <div class="p-d-flex p-ai-center">
              <Checkbox id="employee-extra-charge-confirmation"
                        v-model="billingExtraChargeConfirmed"
                        :binary="true"/>

              <label for="employee-extra-charge-confirmation" class="pointer p-ml-2">
                {{ $t('billing.extraResources.additionalEmployeeConfirmation', {
                    price: employeeResourceUsage.monthly_delta
                  })
                }}
              </label>
            </div>
          </template>
        </InlineNotice>
      </div>
      <div v-if="!item?.id && trialEmployeeExtraNotice" class="p-mt-1 p-pb-3">
        <InlineNotice severity="info" icon="pi pi-info-circle"
                      :title="$t('billing.extraResources.additionalEmployeeTitle')">
          {{ $t('billing.planChangeModal.trialEmployeeNotice', {
            included: employeeResourceUsage.included_current,
            price: employeeResourceUsage.unit_price
          }) }}
        </InlineNotice>
      </div>
      <button type="submit" style="display:none"/>
    </form>


    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <Button :label="$t('Save')"
              :disabled="disableSaveButton ||
                         dataIsSending ||
                         billingResourceUsageIsLoading ||
                         (!item?.id && employeeExtraChargeRequired && !billingExtraChargeConfirmed) ||
                         Boolean(takenUsername && userData.username === takenUsername) ||
                         Boolean(passwordServerError)"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import roles from '@/translations/roles'
// import phoneCodes from '@/translations/phoneCodes'
import settings from '@/settings'
import constants from '@/constants'
import formatMixins from "@/mixins/formatMixins";
import overlayVisibilityMixins from "@/mixins/overlayVisibilityMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import inputsFormatMixins from "@/mixins/inputsFormatMixins";
import PhoneInput from "@/components/PhoneInput";
import EmailInput from "@/components/EmailInput";
import InlineNotice from '@/components/InlineNotice.vue'

export default {
  mixins: [ formatMixins, overlayVisibilityMixins, showErrorsMixins, inputsFormatMixins ],
  components: { PhoneInput, EmailInput, InlineNotice },
  emits: ['close', 'update-items', 'update-item'],
  name: 'EmployeeModal',
  props: {
    item: Object,
    visible: Boolean,
    modalLayer: {
      type: Number,
      default: 1
    }
  },
  data() {
    return {
      emailInvalid: false,
      phoneInvalid: false,
      workPhoneInvalid: false,
      commentMaxChars: 500,
      usernameIsTaken: false,
      takenUsername: null,
      passwordServerError: null,
      contractStartDateIsValid: false,
      contractEndDateIsValid: false,
      birthDateIsValid: false,
      settings,
      submitted: false,
      firstName: null,
      lastName: null,
      userData: {},
      employeeData: {},
      addressDraft: null,
      userAddress: null,
      selectedLanguage: null,
      languageValues: settings.languages,
      createAccount: true,
      // roleValues: null,
      selectedRole: null,
      constants,
      disableSaveButton: false,
      dataIsSending: false,
      // phoneCodesArray: [],
      defaultPhoneCode: null,
      phoneNumberIsTaken: false,
      takenPhoneNumber: null,
      billingResourceUsage: null,
      billingResourceUsageIsLoading: false,
      billingExtraChargeConfirmed: false,
    }
  },
  watch: {
    item(value) {
      // immediate: true,
      // handler(value) {
      if (!value) {
        this.firstName = null
        this.lastName = null
        this.userData = {}
        this.employeeData = {}
        this.selectedRole = null
        this.selectedLanguage = this.computedLocationDefaultLanguage
        return
      }

      this.userData = { ...value }
      this.employeeData = value.employee ? { ...value.employee } : {}

      this.firstName = value.first_name ?? null
      this.lastName = value.last_name ?? null

      if (value.employee) {
        this.employeeData.start = value.employee.start ? new Date(value.employee.start * 1000) : null
        this.employeeData.end = value.employee.end ? new Date(value.employee.end * 1000) : null
      } else {
        this.employeeData.start = null
        this.employeeData.end = null
      }

      this.userData.birthdate = value.birthdate ? new Date(value.birthdate * 1000) : null

      this.checkBirthDateIsValid()
      this.checkContractStartDateIsValid()
      this.checkContractEndDateIsValid()

      if (value.language) {
        this.selectedLanguage = settings.languages.find((item) => item.value === value.language) ?? this.computedLocationDefaultLanguage
      } else {
        this.selectedLanguage = this.computedLocationDefaultLanguage
      }

      if (!value.id && this.employeeData.role) {
        const userRole = roles.find(role => +role.num === +this.employeeData.role)
        if (userRole) {
          this.selectedRole = { ...userRole, name: userRole.name[this.$i18n.locale] }
        } else {
          this.selectedRole = null
        }
      } else {
        this.selectedRole = null
      }

      this.createAccount = true
      this.userData.password = null
      this.userData.password_repeat = null
    },
    visible() {
      if (!this.visible) {
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.createAccount = true
        this.selectedRole = null
        this.selectedLanguage = null
        this.phoneNumberIsTaken = false
        this.takenPhoneNumber = null
        this.usernameIsTaken = false
        this.takenUsername = null
        this.passwordServerError = null
        this.billingResourceUsage = null
        this.billingResourceUsageIsLoading = false
        this.billingExtraChargeConfirmed = false
        if (this.userData) {
          this.userData.password = null
          this.userData.password_repeat = null
        }
      } else if (!this.item?.id) {
        if (!this.selectedLanguage) {
          this.selectedLanguage = this.computedLocationDefaultLanguage
        }
        this.loadBillingResourceUsage()
      }
    },
    createAccount(value) {
      if (!value) {
        this.userData.username = null
        this.userData.password = null
        this.userData.password_repeat = null
        this.usernameIsTaken = false
        this.takenUsername = null
        this.passwordServerError = null
      }
    },
    'userData.username'() {
      this.clearPasswordPersonalDataError()
    },

    'userData.email'() {
      this.clearPasswordPersonalDataError()
    },

    'userData.phone_code'() {
      this.clearPasswordPersonalDataError()
    },

    'userData.phone_number'() {
      this.clearPasswordPersonalDataError()
    },
    'userData.password'() {
      this.passwordServerError = null
    },
  },
  // mounted() {
  //   this.createPhoneCodesArray()
  // },
  methods: {
    formatLanguageName(language) {
      if (!language) return ''
      return `${String(language.value || '').toUpperCase()} — ${language.label}`
    },
    applyBillingResourceUsage(data) {
      this.billingResourceUsage = data?.success ? data : null
      this.billingExtraChargeConfirmed = false
    },
    async loadBillingResourceUsage() {
      if (this.item?.id || this.billingResourceUsageIsLoading) return

      this.billingResourceUsageIsLoading = true

      try {
        const { status, data } = await httpClient.post('billing/resource-usage')

        if (status === 200 && data?.success) {
          this.applyBillingResourceUsage(data)
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.billingResourceUsageIsLoading = false
      }
    },
    handleExtraChargeConfirmationError(err) {
      const response = err?.response

      if (
          response?.status !== 409
          || response?.data?.code !== 'EXTRA_CHARGE_CONFIRMATION_REQUIRED'
      ) {
        return false
      }

      this.applyBillingResourceUsage(response.data.resource_usage)
      this.$toast.add({
        severity: 'warn',
        detail: this.$t('billing.extraResources.confirmationRequired'),
        life: settings.toastLife,
      })

      return true
    },
    clearPasswordPersonalDataError() {
      if (
          this.passwordServerError ===
          'Password must not match username, email or phone number.'
      ) {
        this.passwordServerError = null
      }
    },
    checkBirthDateIsValid() {
      this.birthDateIsValid = this.checkDateIsValid(this.userData?.birthdate)
    },
    checkContractStartDateIsValid() {
      this.contractStartDateIsValid = this.checkDateIsValid(this.employeeData?.start)
    },
    checkContractEndDateIsValid() {
      this.contractEndDateIsValid = this.checkDateIsValid(this.employeeData?.end)
    },
    checkDateIsValid(date) {
      if (!date) return false
      return !!(date instanceof Date && !isNaN(date))
    },
    // createPhoneCodesArray() {
    //   this.phoneCodesArray = phoneCodes.map(code => {
    //     if (code[2] === settings.22phoneCodes.default) {
    //       this.defaultPhoneCode = {
    //         value: code[2],
    //         country: code[0],
    //         flagCode: code[1]
    //       }
    //     }
    //
    //     return {
    //       value: code[2],
    //       country: code[0],
    //       flagCode: code[1]
    //     }
    //   })
    // },
    normalizePhone(value) {
      return typeof value === 'string' ? value.trim() : value
    },
    normalizeEmail(value) {
      return typeof value === 'string' ? value.trim() : value
    },
    async saveItem() {
      if (!this.userData?.id && this.createAccount) {
        this.userData.username = this.normalizeUsername(this.userData.username)
      }

      this.submitted = true

      if (this.phoneInvalid || this.workPhoneInvalid || this.emailInvalid) return false

      const isCreate = !this.userData?.id

      if (isCreate && this.employeeExtraChargeRequired && !this.billingExtraChargeConfirmed) {
        return false
      }

      if (!this.firstName ||
          !this.selectedLanguage ||
          (isCreate && !this.selectedRole) ||
          (this.employeeData.start && this.employeeData.end && (this.employeeData.start > this.employeeData.end)) ||
          (this.userData.birthdate && !this.birthDateIsValid) ||
          (this.employeeData.start && !this.contractStartDateIsValid) ||
          (this.employeeData.end && !this.contractEndDateIsValid) ||
          // this.phoneNumberIsTaken ||
          (isCreate &&
              this.createAccount &&
              this.userData.username &&
              this.takenUsername &&
              this.userData.username === this.takenUsername) ||
          (this.employeeData.comment && this.employeeData.comment.length > this.commentMaxChars)) {
        return false
      }

      if (isCreate && this.createAccount) {
        if (
            !this.userData.username ||
            this.userData.username.length < settings.minUsernameLength ||
            this.userData.username.length > settings.maxUsernameLength ||
            !this.userData.password ||
            this.userData.password.length < settings.minPasswordLength ||
            this.userData.password.length > settings.maxPasswordLength ||
            this.passwordServerError ||
            !this.userData.password_repeat ||
            this.userData.password !== this.userData.password_repeat
        ) {
          return false
        }
      }

      this.disableSaveButton = true
      this.dataIsSending = true

      this.userData.email = this.normalizeEmail(this.userData.email)
      this.userData.phone_number = this.normalizePhone(this.userData.phone_number)
      this.employeeData.work_phone_number = this.normalizePhone(this.employeeData.work_phone_number)

      const saveUserPhoneNumber = this.userData.phone_code && this.userData.phone_number

      const user = {
        id: this.userData?.id ?? null,
        first_name: this.firstName ? this.formatName(this.firstName) : null,
        last_name: this.lastName ? this.formatName(this.lastName) : null,
        email: this.userData.email ? this.strToLowerCase(this.userData.email) : null,
        phone_country: saveUserPhoneNumber ? this.userData.phone_country : null,
        phone_code: saveUserPhoneNumber ? this.userData.phone_code : null,
        phone_number: saveUserPhoneNumber ? this.userData.phone_number : null,
        full_phone_number: saveUserPhoneNumber
            ? String(this.userData.phone_code) + String(this.userData.phone_number)
            : null,
        birthdate: this.userData.birthdate
            ? Math.floor(this.userData.birthdate.getTime() / 1000)
            : null,
        language: this.selectedLanguage.value,
        flag_code: this.selectedLanguage.flagCode,
        user_address: this.userData.address ?? null,
      }

      if (isCreate) {
        user.role = this.selectedRole?.num ?? null
      }

      const saveWorkPhoneNumber = this.employeeData.work_phone_code && this.employeeData.work_phone_number

      const employee = {
        id: this.userData.employee ? this.userData.employee.id : null,
        user_id: this.userData.id,
        position: this.employeeData.position ?? null,
        personal_code: this.employeeData.personal_code ?? null,
        contract: this.employeeData.contract ?? null,
        start: this.employeeData.start
            ? Math.floor(this.employeeData.start.getTime() / 1000)
            : null,
        end: this.employeeData.end
            ? Math.floor(this.employeeData.end.getTime() / 1000)
            : null,
        base_percent: this.employeeData.base_percent,
        work_phone_country: saveWorkPhoneNumber ? this.employeeData.work_phone_country : null,
        work_phone_code: saveWorkPhoneNumber ? this.employeeData.work_phone_code : null,
        work_phone_number: saveWorkPhoneNumber ? this.employeeData.work_phone_number : null,
        work_full_phone_number: saveWorkPhoneNumber
            ? String(this.employeeData.work_phone_code) + String(this.employeeData.work_phone_number)
            : null,
        comment: this.employeeData.comment ? this.employeeData.comment : null,
      }

      let assignment = null

      if (isCreate) {
        assignment = {
          location_id: this.$store.getters.locationId,
          role: this.selectedRole?.num,
        }
      }

      if (isCreate && this.createAccount) {
        user.username = this.userData.username ? this.strToLowerCase(this.userData.username.trim()) : null
        if (this.userData.password) {
          user.password = this.userData.password
        }
      }

      try {
        if (!isCreate) {
          const { status, data } = await httpClient.post(`employee/update`, { user, employee })

          if (status === 200 && data?.success) {
            this.$emit('update-items', this.userData.id)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife})
            this.close()
          } else if (data?.error) {
            if (data.error.detail === 'Phone number has already been taken.') {
              this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('phoneNumberTaken'), life: settings.toastLife})
              this.takenPhoneNumber = user.full_phone_number
              this.phoneNumberIsTaken = true
            } else {
              this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife})
            }
          } else {
            this.showUnknownError()
          }
        } else {
          user.status = 1

          const payload = { user, employee, assignment }

          if (this.employeeExtraChargeRequired) {
            payload.billing_confirmation = {
              confirmed: this.billingExtraChargeConfirmed,
              type: 'extra_employee',
              monthly_delta: this.employeeResourceUsage.monthly_delta,
              currency: this.billingResourceUsage.currency,
            }
          }

          const { status, data } = await httpClient.post(`employee/create`, payload)

          if (status === 201 && data?.success) {
            this.userData.id = data.id
            user.id = data.id
            this.$emit('update-items', this.userData.id)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: settings.toastLife})
            this.close()
          } else if (data?.error) {
            if (data.error.detail === 'Phone number has already been taken.') {
              this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('phoneNumberTaken'), life: settings.toastLife})
              this.takenPhoneNumber = user.full_phone_number
              this.phoneNumberIsTaken = true
            } else if (data.error.detail === 'Username has already been taken.') {
              this.$toast.add({severity:'error', summary: this.$t('Error'), detail: this.$t('usernameTaken'), life: settings.toastLife})
              this.takenUsername = user.username
              this.usernameIsTaken = true
            } else if (this.isPasswordValidationError(data.error.detail)) {
              this.passwordServerError = data.error.detail
              this.$toast.add({
                severity: 'error',
                summary: this.$t('Error'),
                detail: this.translatePasswordValidationError(data.error.detail),
                life: settings.toastLife,
              })
            } else {
              this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife})
            }
          } else {
            this.showUnknownError()
          }
        }
      } catch(err) {
        if (!this.handleExtraChargeConfirmationError(err)) {
          this.showError(err)
        }
      } finally {
        this.dataIsSending = false
        this.disableSaveButton = false
      }
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
    updateAddress(newAddress) {
      this.userData.address = { ...newAddress }
      this.$emit('update-item', this.userData)
    },
    updateItems() {
      this.$emit('update-items', this.userData.id)
    },
  },
  computed: {
    employeeResourceUsage() {
      return this.billingResourceUsage?.resources?.employee ?? null
    },
    trialEmployeeExtraNotice() {
      return this.$store.getters.isTrialTenant
          && !!this.employeeResourceUsage
          && Number(this.employeeResourceUsage.used) >= Number(this.employeeResourceUsage.included_current)
    },
    employeeExtraChargeRequired() {
      return !!this.employeeResourceUsage?.requires_confirmation
    },
    roleOptions() {
      const superAdmin = constants.userRoles.superAdmin
      const owner = constants.userRoles.owner
      const admin = constants.userRoles.admin
      const accountant = constants.userRoles.accountant
      const customer = constants.userRoles.customer
      const employeeWorkshopMaster = constants.userRoles.employeeWorkshopMaster

      return roles
          .filter(role => {
            if (this.$store.getters.isOwnerOrHigher) {
              return +role.num !== superAdmin && +role.num !== customer && +role.num !== employeeWorkshopMaster
            } else if (this.$store.getters.isAdmin || this.$store.getters.isAccountant) {
              return +role.num !== superAdmin &&
                  +role.num !== owner &&
                  +role.num !== admin &&
                  +role.num !== accountant &&
                  +role.num !== customer &&
                  +role.num !== employeeWorkshopMaster
            }
          })
          .map(role => ({
            num: +role.num,
            name: role.name?.[this.$i18n.locale] || role.name?.en || String(role.num)
          }))
    },
    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: 0, width: '95%', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '550px', marginTop: '15px'}
      } else {
        return {position: 'absolute', top: 0, width: '550px'}
      }
    },
    computedDefaultLanguage() {
      return settings.languages.find(language => language.isDefault) ?? null
    },
    computedLocationDefaultLanguage() {
      const locationLanguage = this.$store.getters.locationData?.language

      if (locationLanguage) {
        const found = settings.languages.find(item => item.value === locationLanguage)
        if (found) return found
      }

      return this.computedDefaultLanguage
    },
  },
}
</script>

<style scoped lang="scss">
.p-button-text {
  width: auto;
}

.section-divider {
  display: flex;
  align-items: center;
}

.section-divider__hr {
  flex: 1;
  margin: 0 0 0 12px;
}
</style>